import { useContext, useRef, useState } from 'react'

import { BankingBankAccountFragment } from 'generated/resources/graphql'

import { ImportModal } from 'modules/core/components/ImportModal'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { downloadBlob } from 'modules/core/lib/download'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { ImportTransactionsContext } from 'modules/banking/contexts/ImportTransactionsContext'
import { useGetImportTemplate } from 'modules/banking/hooks/useGetImportTemplate'
import { useUploadImportFile } from 'modules/banking/hooks/useUploadImportFile'
import handleResultWithFlash from 'modules/finance/lib/handleResultWithFlash'

interface ImportTransactionsModalProps {
  bankAccount: BankingBankAccountFragment
  onClose: () => void
}

export const AccountTransactionsImportModal = ({
  bankAccount,
  onClose,
}: ImportTransactionsModalProps) => {
  const { getAccountTransactionsTemplate } = useGetImportTemplate(bankAccount.currency)
  const {
    loading: loadingUpload,
    rowsProcessed,
    errorChecks,
    errors,
    rowsPersisted,
    loadingCheck,
    uploadImportFileToCheck,
    uploadImportFile,
  } = useUploadImportFile()
  const { setNewNumTransactions } = useContext(ImportTransactionsContext)
  const fileDropped = useRef<File | undefined>(undefined)
  const [fileName, setFileName] = useState<string | undefined>()
  const [showInfo, setShowInfo] = useState<boolean>(false)
  const [infoFileMessage, setInfoFileMessage] = useState<string[]>([])
  const [loadingMessage, setLoadingMessage] = useState<string>('')
  const [color, setColor] = useState<'destructive' | 'positive' | 'info'>('info')

  const download = async () => {
    const result = await getAccountTransactionsTemplate()

    const handled = handleResult(result, handleResultWithFlash())

    if (handled.success) {
      if (!handled.data.transactionImportTemplate) return
      const base64File = handled.data.transactionImportTemplate.fileInBase64
      const fileType = handled.data.transactionImportTemplate.fileType

      const fileResponse = await fetch(
        `data:${fileType};base64,${base64File.replace(/^data:.*,/, '')}`
      )

      const blob = await fileResponse.blob()
      downloadBlob(blob, handled.data.transactionImportTemplate.fileName)
    }
  }

  const onDropFile = (bankAccountIdParam: string, files: File[]) => {
    const file = files[0]
    if (!file) {
      setInfoFileMessage([
        i18n.t('banking.import_transactions_modal.incorrect_file_format_message'),
      ])
      setShowInfo(true)
      return
    }

    setFileName(file.name)
    fileDropped.current = file
    uploadImportFileToCheck(bankAccountIdParam, file)
    setLoadingMessage(i18n.t('banking.import_transactions_modal.checking_file_format'))

    //track(trackingEvents.projectManagement.importProjects.uploadFileForProjectImport)
    setShowInfo(false)
  }
  const onSubmit = async () => {
    if (!fileDropped.current || fileDropped.current === undefined) return null

    track(trackingEvents.accounting.banking.importTransactionsBankAccount)

    uploadImportFile(bankAccount.id, fileDropped.current)
    setLoadingMessage(i18n.t('banking.import_transactions_modal.spinner_message'))

    setShowInfo(false)
  }

  useUnsafeEffect(() => {
    if (errorChecks && errorChecks.length > 0) {
      setShowInfo(true)
      setColor('destructive')
      setInfoFileMessage(errorChecks)
    }
  }, [errorChecks])

  useUnsafeEffect(() => {
    if (errors && errors.length > 0) {
      setShowInfo(true)
      setColor('destructive')
      setInfoFileMessage(errors)
    }
  }, [errors])

  useEffect(() => {
    if (rowsProcessed != null) {
      if (rowsProcessed === 0) {
        setColor('info')
        setInfoFileMessage([
          i18n.t('banking.import_transactions_modal.file_ok_but_already_uploaded'),
        ])
      } else {
        setColor('positive')
        setInfoFileMessage([
          i18n.t('banking.import_transactions_modal.x_lines_to_be_persist', {
            count: rowsProcessed,
          }),
        ])
      }
      setShowInfo(true)
    }
  }, [rowsProcessed])

  useUnsafeEffect(() => {
    if (rowsPersisted != null) {
      onClose()
      setNewNumTransactions(rowsPersisted)
    }
  }, [rowsPersisted, onClose, setNewNumTransactions])

  return (
    <ImportModal
      title={i18n.t('banking.import_transactions_modal.title')}
      description={i18n.t('banking.import_transactions_modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      download={download}
      onDropFile={(file) => onDropFile(bankAccount.id, file)}
      fileName={fileName}
      disabled={
        !fileName ||
        loadingCheck ||
        loadingUpload ||
        (errorChecks != null && errorChecks.length > 0)
      }
      loading={loadingCheck || loadingUpload}
      loadingMessage={loadingMessage}
      infoFileMessage={infoFileMessage}
      infoFileMessageColor={color}
      showInfo={showInfo}
    />
  )
}
